import type { AccelSample, DetectorEvent, GpsSample } from './types';
import { GravityEstimator } from './gravity';
import { HitDetector } from './hit-detector';
import { SwerveDetector } from './swerve-detector';

/**
 * Glues the three detector primitives together for a single trip:
 *
 *   accelerometer → GravityEstimator → (aVert, aLateral)
 *                                       ↓               ↓
 *                                  HitDetector    SwerveDetector
 *                                       ↓               ↓
 *                                          DetectorEvent → emit()
 *
 * Pure-data interface — no Capacitor, no DOM, no DB. Wiring to actual
 * sensor sources lives in a separate file (M2 next step). This keeps the
 * detection logic testable in pure Node/jsdom.
 */
export interface TripRecorderOpts {
  deviceClass: string;
  sampleRateHz: number;
  onEvent: (e: DetectorEvent) => void;
}

export class TripRecorder {
  private gravity: GravityEstimator;
  private hit: HitDetector;
  private swerve: SwerveDetector;

  constructor(private readonly opts: TripRecorderOpts) {
    this.gravity = new GravityEstimator();
    this.hit = new HitDetector({
      deviceClass: opts.deviceClass,
      sampleRateHz: opts.sampleRateHz,
    });
    this.swerve = new SwerveDetector({
      deviceClass: opts.deviceClass,
    });
  }

  ingestAccel(s: AccelSample) {
    const { aVert, aLateral } = this.gravity.feed(s);
    // The +x component (device-local) is a *rough* proxy for sign of
    // lateral acceleration. Real sign-of-swerve recovery needs calibration
    // (M2.5). Using s.ax preserves left/right discrimination at the cost
    // of frame-orientation accuracy.
    const hit = this.hit.feed(aVert, s.ts);
    if (hit) this.opts.onEvent(hit);
    const swerve = this.swerve.feed(aLateral, s.ax, s.ts);
    if (swerve) this.opts.onEvent(swerve);
  }

  ingestGps(s: GpsSample) {
    this.hit.setGps(s);
    this.swerve.setGps(s);
  }
}
